// Packages and components
import React, { useCallback, useEffect, useState } from 'react';
import { Box } from 'grommet';
import { Phone } from 'grommet-icons';
import SHDButton from '@ui/Button/SHDButton';
import SHDCallout from '@ui/Callout/SHDCallout';
import SHDCloseButton from '@ui/CloseButton/SHDCloseButton';
import SHDFooter from '@ui/Footer/SHDFooter';
import SHDHeading from '@ui/Heading/SHDHeading';
import SHDInput from '@ui/Input/SHDInput';
import SHDLoader from '@ui/Loader/SHDLoader';
import SHDModalContainer from '@ui/ModalContainer/SHDModalContainer';
import SHDRule from '@ui/Rule/SHDRule';
import SHDText from '@ui/Text/SHDText';

// Helpers and utilities
import { useImportTeam } from '@hooks/useTeam';
import { DateTime } from 'luxon';

// Types and enums
import type { ExternalPlayer } from '@shd/jslib/models/generated/v2/api';
import type { CompleteYourRosterProps, EditRosterField } from './Onboarding.d';
import type { Dict, TeamImportPartner } from '@theme/theme.d';

const CompleteRoster: React.FC<CompleteYourRosterProps> = ({
  team,
  importPartner,
  onContinue,
}) => {
  // State
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const [players, setPlayers] = useState<ExternalPlayer[]>(team?.players || []);
  const [isImporting, setIsImporting] = useState<boolean>(false);

  // Hooks
  const { onImportTeam } = useImportTeam();

  const formFieldConversion = {
    aes: {
      number: 'role_or_jersey',
      firstName: 'full_name',
      lastName: 'full_name',
      phoneNumber: 'phoneNumber',
    },
    pg: {
      number: 'No',
      firstName: 'Name',
      lastName: 'Name',
      phoneNumber: 'phoneNumber',
    },
    teamsnap: {
      number: 'jersey_number',
      firstName: 'first_name',
      lastName: 'last_name',
      phoneNumber: 'phone_numbers',
    },
    usssa: {
      number: 'uniform_number',
      firstName: 'playerFirst',
      lastName: 'playerLast',
      phoneNumber: 'PlaHomePhone',
    },
  };

  const getBlankRawData = (seedData: Dict, partner: TeamImportPartner) => {
    const now = DateTime.now();

    switch (partner) {
      case 'usssa':
        return {
          ...seedData,
          SeasonYear: now.year,
          PlayerLevel: 0,
          bumped: null,
          playerFullName: '',
          playerFirst: '',
          playerLast: '',
          plabirthdate: '2020-01-01T00:00:00',
          teamname: '',
          originalClass: '',
          EndYearClass: '',
          PlaAddDate: '2020-01-01T00:00:00',
          PlaID: 0,
          PlaTeamID: 0,
          PlaDateDeleted: null,
          PlaReasonDeleted: null,
          PlaEmail1: null,
          PlaEmail2: null,
          PlaHomePhone: null,
          PlaWorkPhone: null,
          season_id: 0,
          seqno: 0,
          who_added_player: 0,
          who_removed_player: null,
          pic_blocked: null,
          graduation_year: 0,
          uniform_number: null,
          high_school_last_attended: null,
          _bats: null,
          _throw: null,
          _position_1: null,
          _position_2: null,
          ncaa_clearinghouse_registered: null,
          college_committed: null,
          ncaa_approval_number: null,
          height: null,
          gpa: null,
          act: null,
          sat: null,
          player_weight: null,
          accolades: null,
          favorite_team: null,
          favorite_player: null,
          likes_hobbies: null,
          approved_by_guardian_flag: true,
          guardian_id: null,
          guardian_approved_date: '2025-01-02T13:47:00',
        };
      case 'pg':
        return {
          No: 0,
          Name: '',
          Pos: '',
          B_T: '',
          Grad: 0,
          Ht: '',
          Wt: 0,
          HS: '',
          Hometown: '',
          Rank: '',
          Commitment: '',
        };
      case 'aes':
        return {
          ...seedData,
          full_name: '',
          role_or_jersey: '',
          event_id: 0,
          division_id: 0,
          team_id: 0,
          phoneNumber: '',
        };
      case 'teamsnap':
        return {
          ...seedData,
          id: 0,
          first_name: '',
          last_name: '',
          jersey_number: '0',
          number: '0',
          position: '',
          is_non_player: false,
          is_coach: false,
          is_manager: false,
          user_id: 0,
          phone_numbers: [],
          email_addresses: [],
        };
      default:
    }
  };

  const createBlankPlayer = useCallback(
    (partner: TeamImportPartner) => {
      const seed: Dict = players.length ? players[0].rawData : {};
      const blankRawData = getBlankRawData(seed, partner);

      return {
        firstName: '',
        lastName: '',
        number: '',
        phoneNumber: null,
        position: 'Unknown',
        gradYear: 0,
        rawData: blankRawData,
        source: partner,
      };
    },
    [players]
  );

  const checkForBlankPlayers = useCallback(
    (newPlayers: ExternalPlayer[]) => {
      const blankPlayers = newPlayers.filter((player) => {
        return (
          !player.firstName &&
          !player.lastName &&
          !player.number &&
          !player.phoneNumber
        );
      });
      if (!blankPlayers.length) {
        newPlayers.push(createBlankPlayer(importPartner));
      }

      return newPlayers;
    },
    [createBlankPlayer, importPartner]
  );

  // Handlers
  const handlePlayerUpdate = (
    updatedValue: string,
    playerIndex: number,
    field: EditRosterField
  ) => {
    // NOTE: This is all a temporary/quick fix until the API is refactored. (NKS 2024-12-22).
    let newPlayers = [...players];
    const updatedPlayer = newPlayers[playerIndex];

    if (field === 'number') {
      updatedPlayer.rawData[formFieldConversion[importPartner]?.number] =
        updatedValue;
      updatedPlayer.number = updatedValue;
    }

    if (field === 'firstName') {
      updatedPlayer.firstName = updatedValue;
      if (importPartner === 'aes') {
        const fullName = updatedPlayer.lastName + ',' + updatedValue;
        updatedPlayer.rawData[formFieldConversion[importPartner]?.firstName] =
          fullName;
      } else if (importPartner === 'pg') {
        const fullName = updatedValue + ' ' + updatedPlayer.lastName;
        updatedPlayer.rawData[formFieldConversion[importPartner]?.firstName] =
          fullName;
      } else {
        updatedPlayer.rawData[formFieldConversion[importPartner]?.firstName] =
          updatedValue;
      }
    }

    if (field === 'lastName') {
      updatedPlayer.lastName = updatedValue;
      if (importPartner === 'aes') {
        const fullName = updatedValue + ',' + updatedPlayer.firstName;
        updatedPlayer.rawData[formFieldConversion[importPartner]?.lastName] =
          fullName;
      } else if (importPartner === 'pg') {
        const fullName = updatedPlayer.firstName + ' ' + updatedValue;
        updatedPlayer.rawData[formFieldConversion[importPartner]?.lastName] =
          fullName;
      } else {
        updatedPlayer.rawData[formFieldConversion[importPartner]?.lastName] =
          updatedValue;
      }
    }

    if (field === 'phoneNumber') {
      updatedPlayer.phoneNumber = updatedValue;
      if (importPartner === 'teamsnap') {
        updatedPlayer.rawData[formFieldConversion[importPartner]?.phoneNumber] =
          [updatedValue];
      } else {
        updatedPlayer.rawData[formFieldConversion[importPartner]?.phoneNumber] =
          updatedValue;
      }
    }

    newPlayers = checkForBlankPlayers(newPlayers);
    setPlayers(newPlayers);
  };

  const handlePlayerRemoval = (playerIndex: number) => {
    const newPlayers = [...players];
    newPlayers.splice(playerIndex, 1);
    setPlayers(newPlayers);
  };

  const handleImportTeam = async () => {
    setIsImporting(true);
    const filteredPlayers = players.filter((player) => {
      return (
        player.firstName ||
        player.lastName ||
        player.number ||
        player.phoneNumber
      );
    });

    if (team) {
      if (team.source === 'aes' && team.ageGroup === null) {
        if (team.ageGroup === null) {
          team.ageGroup = '0';
          team.rawTeamData.age_group = '0';
        }
      }
      const updatedTeam = { ...team, players: filteredPlayers };
      await onImportTeam(updatedTeam).then((response) => {
        if (response) {
          onContinue && onContinue(true, response);
        }
      });
    }
  };

  // Effects
  useEffect(() => {
    if (initialLoad) {
      const newPlayers = checkForBlankPlayers(players);
      setPlayers(newPlayers);
      setInitialLoad(false);
    }
    document.querySelectorAll('.player-row').forEach((row, index) => {
      const closeButton = row.querySelector('.player-row__close-button');

      if (closeButton) {
        if (index === players.length - 1) {
          (closeButton as HTMLElement).style.display = 'none';
        } else {
          (closeButton as HTMLElement).style.display = 'block';
        }
      }
    });
  }, [initialLoad, players, checkForBlankPlayers]);

  // Renders
  const renderPlayerRow = (playerIndex: number) => {
    const player = players[playerIndex];
    return (
      <Box
        key={player.rawData.id}
        className="player-row"
        margin={{ bottom: 'xsmall' }}
      >
        <Box direction="row" gap="none" pad={{ horizontal: 'xxsmall' }}>
          <SHDInput
            isBorderless
            label="First name"
            value={player.firstName}
            onChange={(newValue) => {
              handlePlayerUpdate(newValue, playerIndex, 'firstName');
            }}
          />
          <SHDInput
            isBorderless
            label="Last name"
            value={player.lastName}
            onChange={(newValue) => {
              handlePlayerUpdate(newValue, playerIndex, 'lastName');
            }}
          />
        </Box>

        <Box direction="row" gap="large" align="center" height="20px">
          <Box width="87%">
            <SHDRule />
          </Box>
          <Box className="player-row__close-button">
            <SHDCloseButton
              isFilled
              background="black-alpha-50"
              size="xsmall"
              onClick={() => {
                handlePlayerRemoval(playerIndex);
              }}
            />
          </Box>
        </Box>

        <Box
          direction="row"
          gap="none"
          border={{ side: 'bottom', color: 'black-alpha-10' }}
          pad={{ horizontal: 'xxsmall', vertical: 'xxsmall' }}
        >
          <SHDInput
            isBorderless
            label="Phone number"
            value={player.phoneNumber || ''}
            onChange={(newValue) => {
              handlePlayerUpdate(newValue, playerIndex, 'phoneNumber');
            }}
          />
          <SHDInput
            isBorderless
            label="Jersey number"
            value={
              player.rawData[formFieldConversion[importPartner]['number']] || ''
            }
            onChange={(newValue) => {
              handlePlayerUpdate(newValue, playerIndex, 'number');
            }}
          />
        </Box>
      </Box>
    );
  };

  return (
    <>
      <SHDModalContainer>
        <Box direction="column" gap="xsmall" margin={{ bottom: 'small' }}>
          <SHDHeading level={3}>Complete Roster</SHDHeading>
          <SHDText color="gray-900-alpha-80" style="subhead">
            Confirm import and add player phone numbers to send invites.
          </SHDText>
        </Box>

        <SHDCallout
          background="gray-50"
          direction="row"
          pad="small"
          round="medium"
        >
          <Box margin={{ left: 'small' }}>
            {team?.name && (
              <SHDText style="subhead-semibold">{team.name}</SHDText>
            )}
            <Box
              justify="start"
              direction="row"
              gap="xsmall"
              align="center"
              alignContent="center"
            >
              {team?.season && (
                <SHDText style="body-semibold" color="black-alpha-50">
                  est. {team?.season}
                </SHDText>
              )}
              {team?.ageGroup && (
                <>
                  <Box style={{ transform: 'translateY(1px)' }}>
                    <SHDText
                      style="body-semibold"
                      color="black-alpha-50"
                      margin={{ top: 'large' }}
                    >
                      &bull;
                    </SHDText>
                  </Box>
                  <SHDText style="body-semibold" color="black-alpha-50">
                    {team?.ageGroup}
                  </SHDText>
                </>
              )}
            </Box>
          </Box>
        </SHDCallout>

        <Box
          align="center"
          direction="row"
          gap="small"
          margin={{ vertical: 'large' }}
        >
          <Box
            align="center"
            background="gray-50"
            direction="row"
            gap="small"
            pad="small"
            round="medium"
          >
            <Phone size="medium" color="black-alpha-50" />
          </Box>
          <SHDText style="body" color="black-alpha-50">
            Don&apos;t have contacts yet? Don&apos;t worry, you can complete
            this later.
          </SHDText>
        </Box>
      </SHDModalContainer>

      <Box>
        {players?.length === 0 ? (
          <SHDModalContainer>
            <SHDText style="subhead">
              No players were found on this roster. Please add players manually.
            </SHDText>
          </SHDModalContainer>
        ) : (
          <Box
            className="players-list"
            border={{ side: 'top', color: 'black-alpha-10' }}
            pad={{ top: 'xxsmall' }}
          >
            {players?.map((__, index) => {
              return renderPlayerRow(index);
            })}
          </Box>
        )}
      </Box>
      <SHDFooter margin={{ top: 'xlarge' }}>
        <SHDModalContainer>
          <SHDButton
            primary
            icon={
              isImporting ? <SHDLoader color="white" size={21} /> : undefined
            }
            disabled={isImporting}
            label={isImporting ? 'Importing team' : 'Complete'}
            onClick={() => handleImportTeam()}
          />
        </SHDModalContainer>
      </SHDFooter>
    </>
  );
};

export default CompleteRoster;
